import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import { LANDING } from 'constants/testIds';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { y: 24, opacity: 0 },
  show: { y: 0, opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// Masked, line-by-line reveal for the headline
const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineItem = {
  hidden: { y: '110%' },
  show: { y: '0%', transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const HeadlineLine = ({ children }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span variants={lineItem} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  // Scroll parallax
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  // Mouse parallax for the glow
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const gx = useSpring(mx, { stiffness: 60, damping: 20 });
  const gy = useSpring(my, { stiffness: 60, damping: 20 });
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 40);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 40);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid={LANDING.heroSection}
      className="relative overflow-hidden pt-32 md:pt-40 pb-20 md:pb-32"
    >
      {/* Backdrops */}
      <motion.div style={{ y: gridY }} className="absolute inset-0 grid-lines opacity-60" aria-hidden />
      <motion.div style={{ x: gx, y: glowY, translateY: gy }} className="absolute inset-0 hero-glow" aria-hidden />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-zinc-950" aria-hidden />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative mx-auto max-w-7xl px-6 lg:px-10"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 items-center">
          <div className="md:col-span-7">
            {/* Headline — masked line-by-line reveal */}
            <motion.h1
              variants={lineWrap}
              data-testid={LANDING.heroHeadline}
              className="mt-8 font-display font-bold text-zinc-50 text-5xl sm:text-6xl md:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tighter"
            >
              <HeadlineLine>
                A <span className="text-[#6C63F5]">community</span>
              </HeadlineLine>
              <HeadlineLine>for creatives.</HeadlineLine>
              <HeadlineLine>
                A <span className="text-[#6C63F5]">network</span> for brands
                <span className="text-[#6C63F5]">.</span>
              </HeadlineLine>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={item}
              data-testid={LANDING.heroSubheadline}
              className="mt-8 max-w-xl text-lg md:text-xl text-zinc-400 leading-relaxed"
            >
              Create, connect, collaborate with like-minded creatives.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={item} className="mt-10 flex flex-wrap items-center gap-3">
              <button
                data-testid={LANDING.heroCtaPrimary}
                onClick={() => navigate('/signup')}
                className="landing-btn landing-btn-primary landing-btn-lg group"
              >
                Join as a creative
                <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <button
                data-testid={LANDING.heroCtaSecondary}
                onClick={() => navigate('/signup')}
                className="landing-btn landing-btn-secondary landing-btn-lg group"
              >
                Join as a brand
                <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </div>

          {/* Hero collage — desktop only */}
          <motion.div
            variants={item}
            className="hidden md:block md:col-span-5 relative mt-4 md:mt-0"
            aria-hidden
          >
            <div className="absolute -inset-8 rounded-full bg-[#6C63F5]/20 blur-3xl opacity-60 pointer-events-none" />
            <img
              src="/hero-collage.png"
              alt=""
              draggable={false}
              className="relative w-full h-auto select-none"
              style={{ filter: 'drop-shadow(0 20px 40px rgba(108, 99, 245, 0.15))' }}
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
